<!--
/**
 * UserLogin — full port of apps/admin/src/views/UserLogin.vue (Phase 2.2).
 *
 * Parity notes (step-1 inventory in the port report):
 * - API: UserService.login({ pin }, { clockIn: false }) → POST /user/login payload
 *   { pin, clock_in: false } — no working-hours session (offline: no local clock-in;
 *   bcrypt check against local users, reject 'invalid_pin'); TenantService.logout()
 *   → POST /logout {} (online only); Notification.fetchUnread() →
 *   GET /notifications?status=unread&limit=10 on created().
 * - Permissions: UserService.allows('nav_access_admin') gates the post-login redirect
 *   AND the certificate-alert "add new" button (old mixin allows()).
 * - Redirects: success+permission → 'adminDashboard'; success w/o permission →
 *   snackbar general.not_possible_from_web_app (no redirect); manager logout →
 *   'tenantLogin'; cert alert button → 'adminAccount'. All pushes keep .catch(() => {}).
 * - $snackbar: user_does_not_exist (error) on invalid pin; not_possible_from_web_app
 *   (error); connection.internet_offline_message_not_allowed (error, 10s) when
 *   logging out the tenant while offline.
 * - EventBus: emits 'show-notification' with the first unread Notification model
 *   (useAppEvents channel, consumed by components/Notification.vue).
 * - Keyboard: document 'keypress' (Enter → login; character keys append to pin when
 *   the field is not focused) + 'keyup' (Escape clears pin), removed on unmount.
 * - Background: tenant.venue.bg_image, else the plain dark paper (as on the POS — the old
 *   /img/background.png fallback dropped 2026-10-14); Viber support row only for locale 'me'
 *   with appConfig.general.supportNumber.
 * - The old numpad login cell had rowspan="2" with no fifth row — inert, dropped.
 * - Redesign P6.2: restyled onto the POS P02 auth pattern — always-dark island on
 *   token surfaces (old #0f1219/#1d1f29/#ffc71f palette dropped), P02 keycap recipe
 *   (sand circles + solid-primary action keys with the primary shadow), quiet
 *   underline PIN field, lucide support icon (inline Viber SVG dropped), entrance
 *   animations removed (off-system motion).
 * - 2026-10-14 (owner: "like the POS"): the POS P02 screen exactly — the app bar has no
 *   surface or border (56px, on the canvas), the canvas is plain paper, and the PIN field,
 *   keycaps and action keys take the POS sizes (20 / 20 / 14px).
 */
-->
<template>
    <!-- POS P02 auth pattern (redesign P6.2): always-dark island — the local
         `dark` class re-scopes tokens; color asserted on the root (L4). -->
    <div v-if="tenant" class="user-login dark flex h-dvh flex-col overflow-auto bg-muted font-sans text-default">
        <!-- App bar as on the POS PIN screen: no surface or border, on the canvas -->
        <header class="z-10 flex min-h-14 shrink-0 items-center gap-2 px-4">
            <span class="text-panel-title">
                {{ t('general.administration') }}{{ isDemo ? ' DEMO' : '' }}
            </span>

            <div class="flex-1" />

            <div
                v-if="locale === 'me' && appConfig && appConfig.general && appConfig.general.supportNumber"
                class="mr-5 ml-2 flex items-center text-body"
            >
                <UIcon name="i-lucide-message-circle-more" class="size-6" aria-hidden="true" />
                <div class="ml-2">{{ t('general.support_message') }}</div>
                <a
                    class="ml-1 text-(--app-text-accent) underline-offset-2 hover:underline"
                    href="#"
                    @click.prevent.stop="openViberSupport"
                >
                    {{ appConfig.general.supportNumber }}
                </a>
            </div>

            <UDropdownMenu :items="menuItems" :content="{ align: 'end' }" :ui="{ content: 'w-64' }">
                <UButton
                    icon="i-lucide-ellipsis-vertical"
                    variant="ghost"
                    color="neutral"
                    :ui="{ leadingIcon: 'size-5' }"
                    aria-label="Menu"
                />

                <template #account>
                    <div class="flex w-full items-center gap-3 py-1">
                        <UAvatar icon="i-lucide-user" :ui="{ root: 'bg-primary', icon: 'text-white' }" />
                        <div class="min-w-0">
                            <div class="truncate font-semibold">{{ tenant.name }}</div>
                            <div class="text-helper truncate text-muted">{{ t('user_roles.default.super_admin') }}</div>
                        </div>
                    </div>
                </template>
            </UDropdownMenu>
        </header>

        <!-- Main content: dark paper canvas; a venue's own image covers it (P02) -->
        <main class="relative flex min-h-0 flex-1 flex-col bg-muted bg-cover bg-bottom-left" :style="bgStyle">
            <!-- Certificate expiry alert (old: square v-alert type=error, mb-0) -->
            <UAlert
                v-if="tenant && tenant.cert_expire_at && moment(tenant.cert_expire_at).diff(moment(), 'days') < 30"
                class="rounded-none"
                color="error"
                icon="i-lucide-circle-x"
            >
                <template #description>
                    {{ t('general.certificate_expires', { value: moment(tenant.cert_expire_at).format('DD.MM.YYYY.') }) }}
                    <UButton
                        v-if="allows('nav_access_admin')"
                        variant="outline"
                        color="neutral"
                        size="sm"
                        class="ml-2 rounded-full border-white text-white hover:bg-white/10"
                        @click="router.push({ name: 'adminAccount' }).catch(() => {})"
                    >
                        {{ t('general.add_new') }}
                    </UButton>
                </template>
            </UAlert>

            <div class="login-wrapper flex min-w-[275px] flex-1 items-center justify-center">
                <div class="login-container w-[290px]">
                    <div class="logo mb-5 flex justify-center">
                        <img
                            :src="logoUrl"
                            alt=""
                            class="max-h-37.5 max-w-37.5 select-none"
                            draggable="false"
                        >
                    </div>

                    <div class="pin-row mb-4 flex justify-center px-6">
                        <UInput
                            ref="pinField"
                            v-model="pin"
                            type="password"
                            autocomplete="disabled"
                            :placeholder="t('general.pin')"
                            :aria-label="t('general.pin')"
                            variant="none"
                            class="pin-input w-full max-w-[265px]"
                        />
                    </div>

                    <!-- Numpad — POS P02 keycaps: sand circles, action keys solid primary -->
                    <div class="mx-auto grid w-fit grid-cols-3 gap-1.5">
                        <button
                            v-for="key in ['7', '8', '9', '4', '5', '6', '1', '2', '3']"
                            :key="key"
                            type="button"
                            class="numpad-button"
                            @click="input(key)"
                        >
                            {{ key }}
                        </button>

                        <button type="button" class="numpad-button action-btn" @click="pin = ''">
                            {{ t('general.clear') }}
                        </button>

                        <button type="button" class="numpad-button" @click="input('0')">0</button>

                        <button
                            type="button"
                            class="numpad-button action-btn"
                            :disabled="busy"
                            @click="loginUser"
                        >
                            <UIcon v-if="busy" name="i-lucide-loader-circle" class="size-5 animate-spin" />
                            <template v-else>{{ t('general.log_in_short') }}</template>
                        </button>
                    </div>
                </div>
            </div>
        </main>
    </div>
</template>

<script setup lang="ts">
import { computed, ref, useTemplateRef, onBeforeUnmount } from 'vue'
import { useRouter } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { storeToRefs } from 'pinia'
import moment from 'moment-timezone'
import { Locale, TenantService, UserService, Notification } from 'app-core'
import type { DropdownMenuItem } from '@nuxt/ui'
import { useAppStore } from '@/stores/useAppStore'
import { useAppEvents, type AppEventPayloads } from '@/composables/useAppEvents'
import { useToast } from '@/composables/useToast'
import { useLocale } from '@/composables/useLocale'
import { usePermissions } from '@/composables/usePermissions'

const { t } = useI18n()
const router = useRouter()
const toast = useToast()
const appEvents = useAppEvents()
const { allows } = usePermissions()
const { locale } = useLocale()
const { tenant, appConfig, isOnline } = storeToRefs(useAppStore())

// ——— data ————————————————————————————————————————————————————————————————————
const busy = ref(false)
const pin = ref('')
const isDemo = import.meta.env.VITE_ENV === 'staging'
const logoUrl = ref('')

/** UInput exposes `inputRef` (the native input) — used for the old isFocused checks. */
const pinField = useTemplateRef<{ inputRef: HTMLInputElement | null }>('pinField')

// ——— created() ———————————————————————————————————————————————————————————————
document.addEventListener('keypress', backgroundKeyPressHandler)
document.addEventListener('keyup', backgroundKeyUpHandler)

pin.value = ''
logoUrl.value = Locale.getBrandedLogoUrl('dark')

checkNotifications()

// beforeDestroy()
onBeforeUnmount(() => {
    document.removeEventListener('keypress', backgroundKeyPressHandler)
    document.removeEventListener('keyup', backgroundKeyUpHandler)
})

// ——— computed ————————————————————————————————————————————————————————————————
/** Plain dark paper per P02, as on the POS; only a venue-configured image overrides it. */
const bgStyle = computed(() => {
    if (tenant.value && tenant.value.venue && tenant.value.venue.bg_image) {
        return { backgroundImage: `url('${tenant.value.venue.bg_image}')` }
    }

    return {}
})

/** Old v-menu list: tenant label row (avatar + name + super admin) and manager logout. */
const menuItems = computed<DropdownMenuItem[][]>(() => [
    [{ slot: 'account', type: 'label' }],
    [{
        label: t('general.manager_logout'),
        icon: 'i-lucide-log-out',
        onSelect: () => logoutTenant()
    }]
])

// ——— methods —————————————————————————————————————————————————————————————————
/**
 * Handle background key press (old: Enter → login; other character keys append to
 * the pin when the field itself is not focused — 'keypress' only fires for
 * character-producing keys, same as before).
 */
function backgroundKeyPressHandler(event: KeyboardEvent) {
    if (event.key === 'Enter') {
        loginUser()
        return
    }

    // old: this.$refs.pin && !this.$refs.pin.isFocused
    if (pinField.value?.inputRef && document.activeElement !== pinField.value.inputRef) {
        input(event.key)
    }
}

/** Handle background key up (old: Escape clears the pin). */
function backgroundKeyUpHandler(event: KeyboardEvent) {
    if (event.key === 'Escape') {
        pin.value = ''
    }
}

/** Pin key input */
function input(key: string) {
    pin.value = pin.value + key
}

/** Login User */
async function loginUser() {
    if (!pin.value) {
        return
    }

    busy.value = true

    // The admin panel is not where a working day starts (docs/WorkingHoursSessions.md).
    UserService.login({ pin: pin.value }, { clockIn: false })
        .then(() => {
            busy.value = false

            pin.value = ''
            const canAccessAdmin = UserService.allows('nav_access_admin')
            if (canAccessAdmin) {
                router.push({ name: 'adminDashboard' }).catch(() => {})
            } else {
                toast.add({ description: t('general.not_possible_from_web_app'), color: 'error' })
            }
        })
        .catch((error: unknown) => {
            busy.value = false

            // old: error === 'invalid_pin' || error.response?.data?.errors?.pin (long form)
            const serverError = error as { response?: { data?: { errors?: { pin?: unknown } } } } | string
            const hasPinError = typeof serverError === 'object' && serverError !== null
                && !!(serverError.response && serverError.response.data && serverError.response.data.errors && serverError.response.data.errors.pin)

            if (error === 'invalid_pin' || hasPinError) {
                toast.add({ description: t('general.user_does_not_exist'), color: 'error' })
            }
            pin.value = ''
        })
}

/** Logout Tenant */
function logoutTenant() {
    if (!isOnline.value) {
        toast.add({ description: t('connection.internet_offline_message_not_allowed'), color: 'error', duration: 10000 })
        return
    }
    TenantService.logout()
    router.push({ name: 'tenantLogin' }).catch(() => {})
}

/** Open Viber support */
function openViberSupport() {
    window.open(`viber://chat?number=${appConfig.value?.general.supportNumber}`)
    pinField.value?.inputRef?.focus()
}

async function checkNotifications() {
    await Notification.fetchUnread()
    const unreadNotifications = Notification.unread()
    if (unreadNotifications.length) {
        // The runtime payload is the app-core Notification model instance (exactly what
        // the old EventBus emitted); the typed channel only declares the plain fields it
        // guarantees, hence the widening — Notification.vue re-narrows it on its side.
        appEvents.emit('show-notification', {
            notification: unreadNotifications[0] as unknown as AppEventPayloads['show-notification']['notification']
        })
    }
}
</script>

<style scoped>
/* PIN field — POS P02: quiet underline field, centered highlighted text. The sizes below are
 * the POS's (its touch ramp: screen title 20px, key cap 20px, quiet button 14px) — this screen
 * is the POS P02 pattern, so it reads the same on both. */
.pin-input {
    border-bottom: 1px solid var(--ui-border-accented);
}

.pin-input :deep(input) {
    color: var(--ui-text-highlighted);
    text-align: center;
    font-size: 20px;
}

/* Keycaps per POS P02: sand circles with a hairline border; action keys on
 * solid primary with white uppercase labels and the primary shadow. */
.numpad-button {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    height: 75px;
    width: 75px;
    border-radius: 50%;
    background: var(--ui-bg-accented);
    border: 1px solid var(--app-pin-keycap-border);
    color: var(--ui-text-highlighted);
    font-size: var(--type-key-cap);
    font-weight: 600;
    white-space: nowrap;
    cursor: pointer;
    user-select: none;
}

.numpad-button:active {
    background: var(--ui-bg-elevated);
}

.numpad-button:disabled {
    opacity: 0.45;
    pointer-events: none;
}

.numpad-button.action-btn {
    background: var(--ui-primary);
    border: none;
    color: #ffffff; /* text on solid color is always white (design §2) */
    font-size: 14px;
    font-weight: 700;
    letter-spacing: 0.5px;
    text-transform: uppercase;
    box-shadow: var(--app-shadow-primary-btn);
}

.numpad-button.action-btn:active {
    background: var(--app-primary-active);
}
</style>
